// challenge-insert-chrome — the challenge surface's "insert at cursor" code chips
// (friendlier-challenges, Trello d7cn4sN9). A sibling chrome module: no Lit, and it talks to
// the editor ONLY through editor-core's public API (`getEditor()` → the Ace editor, whose
// `insert(text)` writes at the cursor of the ACTIVE file, and `isReadOnly()`).
// editor-core.js / editor-api.js are not touched and the runner contract is unchanged.
//
//   chipifyHtml(html, { disabled })  pure: every short `<code>…</code>` in inlineMarkdown
//                                    output becomes a chip button (challenge-chrome renders
//                                    the "What to add" drawer through it).
//   buildChip(doc, text, { disabled }) one chip element for a plain string (the Expected
//                                    column of a failed expected-output row), or null.
//   wireInsertChips({ core, root })  ONE delegated click listener on `root` — main.js passes
//                                    `document.body`, which holds BOTH the drawer and the
//                                    Check result modal `#challenge-result`.
//
// ⚠️ THE 80-CHARACTER / NO-NEWLINE RULE. authenticity-capture.js flags a single insert of
// >= 80 characters or >= 5 lines as a large insert (its FLAG_MIN_CHARS / FLAG_MIN_LINES), and
// a chip insert is an Ace insert like any other. Rather than add an exemption to the capture,
// only a span UNDER 80 characters with no newline becomes a chip; a longer one stays plain
// `<code>`. Chips are author text, not a paste, so a class with pasting turned off
// (no-paste-chrome.js) still allows them — that module only refuses paste/cut/drop events.
//
// Disabled chips (a read-only editor, or a viewer who is not the work's owner) carry
// `aria-disabled="true"` and insert nothing.

export var CHIP_MAX_CHARS = 80;

function decodeEntities(s) {
  return String(s).replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'").replace(/&amp;/g, '&');
}

function escapeAttr(s) {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// A string can be a chip when it is non-empty, shorter than the capture's flag threshold and
// a single line.
export function chipEligible(text) {
  return typeof text === 'string' && text.length > 0 && text.length < CHIP_MAX_CHARS &&
    text.indexOf('\n') === -1 && text.indexOf('\r') === -1;
}

export function chipAriaLabel(text) {
  return 'Insert ' + text + ' at the cursor';
}

// chipifyHtml(html, { disabled }) — `html` is inlineMarkdown output, whose `<code>` bodies
// are already HTML-escaped text with no tags inside. The chip's visible text is that same
// escaped body; `data-insert` holds the DECODED text, which is exactly what gets inserted.
export function chipifyHtml(html, opts) {
  var disabled = !!(opts && opts.disabled);
  return String(html == null ? '' : html).replace(/<code>([^<]*)<\/code>/g, function (whole, body) {
    var text = decodeEntities(body);
    if (!chipEligible(text)) { return whole; }
    return '<button type="button" class="challenge-chip" data-insert="' + escapeAttr(text) + '"' +
      ' aria-label="' + escapeAttr(chipAriaLabel(text)) + '" title="Insert at cursor"' +
      (disabled ? ' aria-disabled="true"' : '') + '>' + body + '</button>';
  });
}

// buildChip(doc, text, { disabled }) — an empty chip button for `text` (the caller appends
// the visible content, e.g. the line with its differing span marked), or null when `text`
// is not chip-eligible.
export function buildChip(doc, text, opts) {
  if (!chipEligible(text)) { return null; }
  var btn = doc.createElement('button');
  btn.type = 'button';
  btn.className = 'challenge-chip';
  btn.setAttribute('data-insert', text);
  btn.setAttribute('aria-label', chipAriaLabel(text));
  btn.setAttribute('title', 'Insert at cursor');
  if (opts && opts.disabled) { btn.setAttribute('aria-disabled', 'true'); }
  return btn;
}

function closestChip(target) {
  return target && typeof target.closest === 'function' ? target.closest('.challenge-chip') : null;
}

// The click handler, exported for the host-only spec.
export function handleChipClick(core, e) {
  var chip = closestChip(e && e.target);
  if (!chip) { return false; }
  // A chip never acts as part of anything around it (a requirement row, the modal).
  if (typeof e.stopPropagation === 'function') { e.stopPropagation(); }
  if (typeof e.preventDefault === 'function') { e.preventDefault(); }
  if (chip.getAttribute('aria-disabled') === 'true') { return false; }
  if (!core || (typeof core.isReadOnly === 'function' && core.isReadOnly())) { return false; }
  var editor = typeof core.getEditor === 'function' ? core.getEditor() : null;
  if (!editor || typeof editor.insert !== 'function') { return false; }
  var text = chip.getAttribute('data-insert');
  if (text == null) { text = chip.textContent || ''; }
  // A chip inside the Check result modal closes the modal first, so the student lands in
  // the code with the inserted text; a drawer chip leaves the drawer open.
  var modal = typeof chip.closest === 'function' ? chip.closest('#challenge-result') : null;
  if (modal) { modal.hidden = true; }
  editor.insert(text);
  if (typeof editor.focus === 'function') { editor.focus(); }
  return true;
}

export function wireInsertChips(opts) {
  var core = opts && opts.core;
  var root = opts && opts.root;
  if (!core || !root || typeof root.addEventListener !== 'function') { return; }
  root.addEventListener('click', function (e) { handleChipClick(core, e); });
}
